import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, startOfDay, endOfDay, startOfMonth, endOfMonth } from 'date-fns';
import { Download, Loader2, Printer, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { DateRange } from 'react-day-picker';
import { fetchAccountStatement } from '@/app/services/reportService';
import { AccountStatementResponse } from '@/app/types/reports';
import { AccountPickerTree } from '@/app/components/common/AccountPickerTree';

export default function AccountStatement() {
  const today = new Date();
  const [accountId, setAccountId] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: startOfMonth(today),
    to: endOfMonth(today)
  });

  const { data: statement, isLoading, error } = useQuery({
    queryKey: ['account-statement', accountId, dateRange?.from, dateRange?.to],
    queryFn: () => fetchAccountStatement({
      account_id: accountId!,
      start_date: format(dateRange?.from || new Date(), 'yyyy-MM-dd'),
      end_date: format(dateRange?.to || dateRange?.from || new Date(), 'yyyy-MM-dd'),
    }),
    enabled: Boolean(accountId && dateRange?.from),
  });


  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    if (!statement) return;
    
    // Create CSV content
    const headers = ['Date', 'Voucher Type', 'Voucher No', 'Particulars', 'Contra Account', 'Debit', 'Credit', 'Balance'];
    const rows = statement.entries.map(entry => [
      format(new Date(entry.date), 'dd/MM/yyyy'),
      entry.voucher_type,
      entry.voucher_no || '',
      entry.particulars,
      entry.contra_account || '',
      entry.debit,
      entry.credit,
      entry.running_balance,
    ]);

    const csvContent = [
      `Account Statement - ${statement.account_name}`,
      `Account Group: ${statement.account_group}`,
      `Account Type: ${statement.account_type}`,
      `Period: ${dateRange?.from ? format(dateRange.from, 'dd/MM/yyyy') : ''} to ${dateRange?.to ? format(dateRange.to, 'dd/MM/yyyy') : ''}`,
      '',
      headers.join(','),
      ...rows.map(row => row.join(',')),
      '',
      `Opening Balance,,,,,,,${statement.opening_balance} ${statement.opening_balance_type}`,
      `Total Debit,,,,,${statement.total_debit},,`,
      `Total Credit,,,,,,${statement.total_credit},`,
      `Closing Balance,,,,,,,${statement.closing_balance} ${statement.closing_balance_type}`,
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `account-statement-${statement.account_name.replace(/\s+/g, '-')}-${format(dateRange?.from || new Date(), 'yyyy-MM-dd')}-to-${format(dateRange?.to || dateRange?.from || new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const formatAmount = (amount: string) => {
    const num = parseFloat(amount);
    return num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="space-y-6">
      {/* Account Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Select Account</CardTitle>
        </CardHeader>
        <CardContent>
          <AccountPickerTree
            value={accountId}
            onChange={setAccountId}
            placeholder="Select an account to view statement..."
          />
        </CardContent>
      </Card>

      {/* Date Range Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Statement Period</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <DateRangePicker
              date={dateRange}
              onDateChange={setDateRange}
            />
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handlePrint}
                disabled={!statement || statement.entries.length === 0}
                title="Print Statement"
              >
                <Printer className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleExport}
                disabled={!statement || statement.entries.length === 0}
                title="Export to CSV"
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statement Display */}
      {!accountId ? (
        <Alert>
          <FileText className="h-4 w-4" />
          <AlertDescription>Please select an account to view its statement.</AlertDescription>
        </Alert>
      ) : isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load statement: {(error as any).message}
          </AlertDescription>
        </Alert>
      ) : statement && statement.entries.length > 0 ? (
        <Card className="print:shadow-none">
          <CardContent className="p-6">
            {/* Header Information */}
            <div className="mb-6 print:mb-8">
              <h2 className="text-xl font-semibold tracking-tight mb-2">Account Statement</h2>
              <div className="border-b pb-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="font-semibold text-lg">{statement.account_name}</p>
                    <p className="text-sm text-muted-foreground">Account Group: {statement.account_group}</p>
                    <p className="text-sm text-muted-foreground">Account Type: {statement.account_type}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">
                      Period: {format(new Date(statement.start_date), 'dd/MM/yyyy')} to {format(new Date(statement.end_date), 'dd/MM/yyyy')}
                    </p>
                    <div className="mt-2 space-y-1">
                      <p className="text-sm font-medium">
                        Opening Balance: ₹ {formatAmount(statement.opening_balance)} ({statement.opening_balance_type})
                      </p>
                      <p className="text-sm font-medium">
                        Closing Balance: ₹ {formatAmount(statement.closing_balance)} ({statement.closing_balance_type})
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Balance Type Legend */}
            <div className="mb-4 p-3 bg-muted rounded-lg print:bg-gray-100">
              <p className="text-sm text-muted-foreground">
                <strong>Balance Types:</strong> 
                {statement.account_type === 'Asset' || statement.account_type === 'Expense' ? (
                  <> Dr (Debit) = Positive balance, Cr (Credit) = Negative balance</>
                ) : (
                  <> Cr (Credit) = Positive balance, Dr (Debit) = Negative balance</>
                )}
              </p>
            </div>

            {/* Statement Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2">Date</th>
                    <th className="text-left py-2 px-2">Type</th>
                    <th className="text-left py-2 px-2">Voucher No.</th>
                    <th className="text-left py-2 px-2">Particulars</th>
                    <th className="text-left py-2 px-2">Contra Account</th>
                    <th className="text-right py-2 px-2">Debit</th>
                    <th className="text-right py-2 px-2">Credit</th>
                    <th className="text-right py-2 px-2">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {statement.entries.map((entry, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-2 px-2">{format(new Date(entry.date), 'dd/MM/yyyy')}</td>
                      <td className="py-2 px-2">{entry.voucher_type}</td>
                      <td className="py-2 px-2">{entry.voucher_no || '-'}</td>
                      <td className="py-2 px-2">{entry.particulars}</td>
                      <td className="py-2 px-2">{entry.contra_account || '-'}</td>
                      <td className="text-right py-2 px-2">
                        {parseFloat(entry.debit) > 0 ? `₹ ${formatAmount(entry.debit)}` : ''}
                      </td>
                      <td className="text-right py-2 px-2">
                        {parseFloat(entry.credit) > 0 ? `₹ ${formatAmount(entry.credit)}` : ''}
                      </td>
                      <td className="text-right py-2 px-2">
                        ₹ {formatAmount(entry.running_balance)}
                      </td>
                    </tr>
                  ))}
                  {/* Totals Row */}
                  <tr className="font-semibold border-t-2">
                    <td colSpan={5} className="py-2 px-2">Total:</td>
                    <td className="text-right py-2 px-2">₹ {formatAmount(statement.total_debit)}</td>
                    <td className="text-right py-2 px-2">₹ {formatAmount(statement.total_credit)}</td>
                    <td className="text-right py-2 px-2"></td>
                  </tr>
                  {/* Closing Balance Row */}
                  <tr className="font-semibold">
                    <td colSpan={7} className="py-2 px-2 text-right">Closing Balance:</td>
                    <td className="text-right py-2 px-2">
                      ₹ {formatAmount(statement.closing_balance)} ({statement.closing_balance_type})
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : accountId && !isLoading && !error ? (
        <Alert>
          <AlertDescription>No transactions found for the selected period.</AlertDescription>
        </Alert>
      ) : null}

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          @page {
            margin: 20mm;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          
          .print\\:mb-8 {
            margin-bottom: 2rem !important;
          }
          
          .print\\:bg-gray-100 {
            background-color: #f3f4f6 !important;
          }
        }
      `}</style>
    </div>
  );
}